import * as React from 'react'
import { Input as InputPrimitive } from '@base-ui/react/input'

import { cn } from '../lib/utils'

function Input({ className, type, ...props }: React.ComponentProps<'input'>) {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      // Autofill bullets:
      //   Chrome paints autofilled inputs with a hard yellow bg that
      //   ignores `background-color` — it can only be overridden via
      //   `-webkit-box-shadow inset`. We paint a 1000px inset shadow
      //   in the page bg colour to mask the yellow, keep the typed
      //   text in the foreground colour, and stretch the bg-color
      //   transition to ~forever so the yellow doesn't flash on
      //   first paint. The saved-emails picker still appears — only
      //   the visual disruption to the input is suppressed.
      // `rounded-lg` matches the `<Select>` trigger so a form
      // mixing inputs and selects reads as one consistent control
      // shape. Padding tuned to the same trigger spec (px-3) so the
      // inner cursor lines up vertically across the row.
      className={cn(
        // Focus state matches `<Textarea>` — neutral `--ring` token
        // (a calm grey across themes) plus a thicker border. Stays
        // venue-agnostic so dark brand colours don't paint a black
        // ring, while keeping a unified language across all form
        // fields (Input / Textarea / PhoneInput).
        // Mobile font-size is `text-base` (16px) on purpose: iOS
        // Safari zooms the viewport into any input whose font-size
        // is < 16px on focus, and doesn't zoom back out when the
        // keyboard dismisses. Holding 16px on mobile + dropping to
        // text-sm (14px) on md+ avoids the bug while keeping the
        // tighter desktop typography.
        'h-10 w-full min-w-0 rounded-lg border border-border bg-transparent px-3 py-2 text-base transition-colors outline-none placeholder:text-foreground/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
        // File inputs: lay the field out as a flex row so the
        // "Choose file" button + filename are vertically centred in the
        // control (stock styling left the button top-aligned). The
        // button mirrors the field type, with a gap before the filename.
        '[&[type=file]]:flex [&[type=file]]:items-center [&[type=file]]:py-0 file:mr-3 file:inline-flex file:h-7 file:items-center file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        'autofill:[-webkit-box-shadow:0_0_0_1000px_var(--background)_inset]! autofill:[-webkit-text-fill-color:var(--foreground)]! autofill:[caret-color:var(--foreground)] autofill:[transition:background-color_9999s_ease-in-out_0s]',
        className
      )}
      {...props}
    />
  )
}

export { Input }
